/**
 * The public MCP server catalog at /mcp-servers. Plain data with no React in it,
 * so the prerenderer and sitemap (vite-plugins/routes.ts) read the same list
 * the app does.
 *
 * catalog.json is generated: run `node scripts/sync-catalog.mjs` against a
 * Statio-Catalog checkout, or let .github/workflows/catalog-sync.yml open the
 * PR. Do not edit it by hand; the next sync overwrites it.
 *
 * Every server gets a page, but a page only goes into the sitemap and lets
 * search engines index it once it has a guide in
 * src/content/servers/<slug>.mdx. Three hundred pages that differ only by name
 * and hostname are the pattern Google treats as scaled content, so the bare
 * ones stay noindex until someone writes them.
 */
import data from './catalog.json';

export type CatalogCategory =
  | 'payments'
  | 'crm'
  | 'developer_tools'
  | 'productivity'
  | 'analytics'
  | 'communication'
  | 'storage'
  | 'ai'
  | 'other';

export interface CatalogServer {
  /** URL segment: /mcp-servers/<slug>. Matches the slug in the app. */
  slug: string;
  name: string;
  description: string;
  category: CatalogCategory;
  /** Who runs the server: Statio's runtime, or the vendor's own endpoint. */
  hosting: 'statio' | 'vendor';
  /** How an install connects: sign in with the vendor, paste a key, or nothing. */
  auth: 'oauth' | 'key' | 'none';
  publisher?: string;
  iconUrl?: string;
  docsUrl?: string;
  tags?: string[];
  /** Labels of the credentials an install asks for. */
  credentials?: string[];
  /** Labels of the non-secret settings an install asks for. */
  settings?: string[];
  /** Statio-run servers only: the hosts the server may reach. */
  egress?: string[];
  /** Statio-run servers only: the host typed in at install is allowed too. */
  egressFromSetting?: boolean;
}

export const CATALOG = data as CatalogServer[];

/** The app's labels, in the app's order (Pugio PreBuilts.tsx). */
export const CATEGORY_LABELS: Record<CatalogCategory, string> = {
  payments: 'Payments',
  crm: 'CRM',
  developer_tools: 'Developer Tools',
  productivity: 'Productivity',
  analytics: 'Analytics',
  communication: 'Communication',
  storage: 'Storage',
  ai: 'AI',
  other: 'Other',
};

export const CATALOG_PATH = '/mcp-servers';

export const serverPath = (slug: string) => `${CATALOG_PATH}/${slug}`;
